/**
 * Feature Toggle System with Infrastructure Dependencies
 *
 * Features can declare infrastructure dependencies, which drives:
 * - Which Docker services start (`docker-compose --profile`)
 * - Which tests run (Jest + Playwright auto-skip)
 * - Runtime validation (fail early if dependency missing)
 */

// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

export interface FeatureDependencies {
  /** Requires PostgreSQL database */
  database?: boolean
  /** Requires SES/MailHog email service */
  email?: boolean
  /** Requires S3 storage (via LocalStack or AWS) */
  storage?: boolean
  /** Requires LocalStack (S3, STS, IAM) */
  localstack?: boolean
  /** Requires OTEL collector for tracing/metrics */
  monitoring?: boolean
  /** Requires Elasticsearch cluster (Stage 5 CQRS read model) */
  elasticsearch?: boolean
  /** Requires pg-boss worker (Stage 5 outbox relay + sync pipeline) */
  pgboss?: boolean
  /** External service URLs to validate (future use) */
  external?: string[]
}

export interface FeatureConfig {
  /** Whether the feature is enabled */
  enabled: boolean
  /** If true, feature is excluded in production */
  devOnly: boolean
  /** Human-readable description */
  description: string
  /** Infrastructure dependencies required by this feature */
  dependencies: FeatureDependencies
}

// ──────────────────────────────────────────────────────────
// Feature Definitions
// ──────────────────────────────────────────────────────────

export const features = {
  admin: {
    enabled: true,
    devOnly: false,
    description: 'Admin dashboard with user/role management',
    dependencies: { database: true },
  },
  profile: {
    enabled: true,
    devOnly: false,
    description: 'User profile page',
    dependencies: { database: true },
  },
  docs: {
    enabled: true,
    devOnly: false,
    description: 'Markdown documentation viewer',
    dependencies: {},
  },
  devtools: {
    enabled: true,
    devOnly: true,
    description: 'Developer tools for testing auth, email, and monitoring',
    dependencies: { email: true, localstack: true, monitoring: true },
  },
  about: {
    enabled: true,
    devOnly: false,
    description: 'Application information and diagnostics',
    dependencies: {},
  },
  entitlements: {
    enabled: true,
    devOnly: false,
    description: 'Entitlement management and seat allocation',
    dependencies: { database: true },
  },
  m2m: {
    enabled: true,
    devOnly: false,
    description: 'Machine-to-machine API authentication',
    dependencies: { database: true },
  },
  auth: {
    enabled: true,
    devOnly: false,
    description: 'Self-service registration and authentication',
    dependencies: { database: true },
  },
  files: {
    enabled: true,
    devOnly: false,
    description: 'File upload and download with user-scoped storage',
    dependencies: { storage: true },
  },
  notifications: {
    enabled: true,
    devOnly: false,
    description: 'In-app notification system',
    dependencies: { database: true },
  },
  jobs: {
    enabled: true,
    devOnly: false,
    description: 'Background job queue for async processing',
    dependencies: {},
  },
  invitations: {
    enabled: true,
    devOnly: false,
    description: 'Pre-auth gate invitation management for user onboarding',
    dependencies: { database: true },
  },
  tenants: {
    enabled: true,
    devOnly: false,
    description: 'Tenant aggregate, data ownership, and sister-company relationships',
    dependencies: { database: true },
  },
  permissions: {
    enabled: true,
    devOnly: false,
    description: 'Typed permission registry and per-user overrides',
    dependencies: { database: true },
  },
  settings: {
    enabled: true,
    devOnly: false,
    description: 'Three-scope settings (system → tenant → user)',
    dependencies: { database: true },
  },
  media: {
    enabled: true,
    devOnly: false,
    description: 'Tenant-scoped media library catalog',
    dependencies: { database: true, storage: true },
  },
  notificationTemplates: {
    enabled: true,
    devOnly: false,
    description: 'Tenant-scoped notification template registry and dispatcher',
    dependencies: { database: true },
  },
  auditEvents: {
    enabled: true,
    devOnly: false,
    description: 'DB-mirrored audit event browser for admins',
    dependencies: { database: true },
  },
  eula: {
    enabled: true,
    devOnly: false,
    description: 'Versioned EULA documents and per-user acceptance tracking',
    dependencies: { database: true },
  },
  subscriptions: {
    enabled: true,
    devOnly: false,
    description: 'Subscription tiers + tenant subscriptions + feature/quota gates',
    dependencies: { database: true },
  },
  registration: {
    enabled: true,
    devOnly: false,
    description: 'Public self-service registration saga (start/verify/complete)',
    dependencies: { database: true, email: true },
  },
  onboarding: {
    enabled: true,
    devOnly: false,
    description: 'Tenant onboarding checklist and wizard backend',
    dependencies: { database: true },
  },
  companyTypeProfile: {
    enabled: true,
    devOnly: false,
    description: 'Type-specific tenant metadata (mfr/dealer/distributor/rep)',
    dependencies: { database: true },
  },
  currencies: {
    enabled: true,
    devOnly: false,
    description: 'Currencies, FX rates, and conversion service',
    dependencies: { database: true },
  },
  vendors: {
    enabled: true,
    devOnly: false,
    description: 'Vendor master with discounts, FOBs, attachments and CD-pricing',
    dependencies: { database: true },
  },
  manufacturers: {
    enabled: true,
    devOnly: false,
    description: 'Manufacturer master view, brands and audit history',
    dependencies: { database: true },
  },
  manufacturerSettings: {
    enabled: true,
    devOnly: false,
    description:
      'Manufacturer Settings module: commercial terms, discounts, tenant copy-on-write overrides of shared/public manufacturers',
    dependencies: { database: true },
  },
  // Stage 4 — Catalog, pricing, warehouse, drawings, custom fields, utilities
  categories: {
    enabled: true,
    devOnly: false,
    description: 'Vendor-scoped product category tree with synonyms and fuzzy search',
    dependencies: { database: true },
  },
  products: {
    enabled: true,
    devOnly: false,
    description: 'Product aggregate with multi-tenant visibility and outbox events',
    dependencies: { database: true },
  },
  pricing: {
    enabled: true,
    devOnly: false,
    description: 'Pure pricing engine + quote endpoints',
    dependencies: { database: true },
  },
  warehouses: {
    enabled: true,
    devOnly: false,
    description: 'Warehouses, stock and inventory-feed model aliases',
    dependencies: { database: true },
  },
  inventory: {
    enabled: true,
    devOnly: false,
    description:
      'Inventory Manager: dealer-facing AQ vs Custom product workspace, KPIs, CSV import, settings (composes products + warehouses)',
    // pgboss/elasticsearch power import-apply reindex; database is the hard floor
    dependencies: { database: true, pgboss: true, elasticsearch: true },
  },
  privateCatalog: {
    enabled: true,
    devOnly: false,
    description: 'Per-tenant catalog membership for filtered visibility',
    dependencies: { database: true },
  },
  customFields: {
    enabled: true,
    devOnly: false,
    description: 'Typed custom field definitions and values',
    dependencies: { database: true },
  },
  // Stage 5 — Catalog CQRS (browse / search / sync / volume)
  outboxRelay: {
    enabled: true,
    devOnly: false,
    description: 'Outbox dispatcher → projection handlers (CQRS read model)',
    dependencies: { database: true, pgboss: true },
  },
  catalogSearch: {
    enabled: true,
    devOnly: false,
    description: 'Elastic-backed catalog search + facets + PDP',
    dependencies: { database: true, elasticsearch: true },
  },
  catalogSync: {
    enabled: true,
    devOnly: false,
    description: 'Vendor catalog data sync pipeline (download/parse/normalize/load)',
    dependencies: { database: true, pgboss: true, storage: true },
  },
  configuration: {
    enabled: true,
    devOnly: false,
    description:
      'Product configuration pipeline (CDN JSON → parse → match → tree → price)',
    dependencies: { database: true, elasticsearch: true },
  },
  manufacturerVolume: {
    enabled: true,
    devOnly: false,
    description: 'Manufacturer volume snapshots (projects / orders aggregation)',
    dependencies: { database: true, pgboss: true },
  },
  // Stage 6 — Contacts / CRM
  contacts: {
    enabled: true,
    devOnly: false,
    description:
      'Companies + people + typed addresses + customer numbers + CRM opportunities (unified CRM aggregate)',
    dependencies: { database: true },
  },
  contactsSearch: {
    enabled: true,
    devOnly: false,
    description: 'Elasticsearch-backed contacts search (CQRS read model with PG fallback)',
    dependencies: { database: true, elasticsearch: true },
  },
  contactsImport: {
    enabled: true,
    devOnly: false,
    description: 'CSV contact import pipeline (streaming parse + mapping profiles + error report)',
    dependencies: { database: true, pgboss: true, storage: true },
  },
  projects: {
    enabled: true,
    devOnly: false,
    description:
      'Projects / Quotes core: header + lifecycle + line items + totals + versions + shares + POs',
    dependencies: { database: true },
  },
  projectsSearch: {
    enabled: true,
    devOnly: false,
    description: 'Elasticsearch-backed project list/search (CQRS read model with PG fallback)',
    dependencies: { database: true, elasticsearch: true },
  },
  dashboard: {
    enabled: true,
    devOnly: false,
    description: 'Dealer Dashboard: composite read over the projects ES read model + Postgres',
    // `elasticsearch` is NOT declared: four sections read ES, but the whole route
    // degrades to Postgres per-section rather than failing, so the cluster is not a
    // hard startup dependency. Declaring it would gate the feature (and its tests) on
    // infrastructure it is explicitly designed to survive the loss of.
    dependencies: { database: true },
  },
  projectsReminders: {
    enabled: true,
    devOnly: false,
    description: 'Hourly pg-boss worker that dispatches due project reminders',
    dependencies: { database: true, pgboss: true },
  },
  projectOperations: {
    enabled: true,
    devOnly: false,
    description:
      'Project operations: history, diff, JSON/XLSX import & export, reviews, report templates',
    dependencies: { database: true, pgboss: true, storage: true },
  },
  projectExportPdf: {
    enabled: true,
    devOnly: false,
    description: 'PDF report rendering via pdfkit + handlebars templates',
    dependencies: { database: true, pgboss: true, storage: true },
  },
  // Stage 9 — Open Public API
  apiKeys: {
    enabled: true,
    devOnly: false,
    description:
      'Tenant-minted long-lived API keys (admin issuance + verification) backing the Open Public API',
    dependencies: { database: true },
  },
  publicApi: {
    enabled: true,
    devOnly: false,
    description:
      'Customer-facing /api/public/v1 surface for Products, Inventory, and triggerTenantSync',
    // pgboss enables the catalog.reindex.tenant worker and elasticsearch
    // is required for the actual reindex to succeed; database is the
    // hard floor (api keys + idempotency live in PG).
    dependencies: { database: true, pgboss: true, elasticsearch: true },
  },
} as const satisfies Record<string, FeatureConfig>

export type FeatureName = keyof typeof features

// ──────────────────────────────────────────────────────────
// Feature State Queries
// ──────────────────────────────────────────────────────────

/**
 * Check if a feature is enabled, accounting for devOnly in production
 */
export function isFeatureEnabled(name: FeatureName, isProduction = false): boolean {
  const feature = features[name]
  if (!feature?.enabled) return false
  if (feature.devOnly && isProduction) return false
  return true
}

/**
 * Check if a feature is development-only
 */
export function isDevFeature(name: FeatureName): boolean {
  return features[name]?.devOnly ?? false
}

/**
 * Get list of enabled feature names
 */
export function getEnabledFeatureNames(isProduction = false): FeatureName[] {
  return (Object.keys(features) as FeatureName[]).filter((name) =>
    isFeatureEnabled(name, isProduction)
  )
}

// ──────────────────────────────────────────────────────────
// Infrastructure Dependency Resolution
// ──────────────────────────────────────────────────────────

/**
 * Compute the set of infrastructure dependencies required by all enabled features.
 * Used to determine which Docker services to start and which env vars are required.
 */
export function getRequiredInfrastructure(
  isProduction = false
): Set<keyof FeatureDependencies> {
  const required = new Set<keyof FeatureDependencies>()

  for (const name of getEnabledFeatureNames(isProduction)) {
    const deps = features[name].dependencies
    // Use 'in' operator to safely check properties on narrowed const types
    if ('database' in deps && deps.database) required.add('database')
    if ('email' in deps && deps.email) required.add('email')
    if ('storage' in deps && deps.storage) required.add('storage')
    if ('localstack' in deps && deps.localstack) required.add('localstack')
    if ('monitoring' in deps && deps.monitoring) required.add('monitoring')
  }

  return required
}

/**
 * Get the dependencies for a specific feature
 */
export function getFeatureDependencies(name: FeatureName): FeatureDependencies {
  return features[name]?.dependencies ?? {}
}
